import { useState } from "react";
import { Link } from "react-router";

import { queryKeys } from "@/api/hooks";
import { splitPartKey } from "@/lib/assetContent";
import { isTerminalQueryError } from "@/api/queryClient";
import {
  useAssetVersionsQuery,
  useLibraryPartManifestQuery,
  usePathwayFamiliesQuery,
  type LibraryAsset,
  type PathwayAsset,
  type PathwayFamily,
  type PathwayVariantAsset,
} from "@/api/library";
import { useRetryPausedQuery } from "@/api/useRetryPausedQuery";
import { useCapability } from "@/auth/SessionContext";
import { lifecycleRefusal } from "@/lib/archiveRefusal";
import { libraryAssetHref } from "@/lib/librarySections";
import {
  addNamedRole,
  addableRoles,
  findNamedRow,
  manifestFor,
  moveNamedRow,
  namedRows,
  removeNamedRow,
  unremovableRoles,
  updateNamedDocument,
} from "@/lib/namedParts";
import { pathwayFamilyOfAsset, stepCounts } from "@/lib/pathwayFamilies";
import {
  emptyScreeningDocument,
  screeningAddRefusal,
  referencedStepIds,
  screeningStepIds,
  screeningSchemaForRole,
  serializeScreeningDocument,
  typedScreeningRoles,
  type ScreeningDocumentSchema,
} from "@/lib/screeningDocuments";
import { PageHeader } from "@/shell/PageHeader";
import { Button } from "@/ui/Button";
import { Tooltip } from "@/ui/Tooltip";
import { AssetAuditTrail } from "@/ui/library/AssetAuditTrail";
import { SaveStatusLine } from "@/ui/library/AssetEditor";
import { LifecycleDialog, type LifecycleMode } from "@/ui/library/LifecycleDialog";
import {
  LoadingState,
  PartialDataNotice,
  UnavailableState,
} from "@/ui/states";

import { NamedAssetMeta } from "./NamedAssetChrome";
import {
  NamedDocumentActions,
  NamedDocumentEditor,
  isAuthorableDocumentSet,
} from "./NamedDocumentEditor";
import {
  NamedConflictRecovery,
  NamedProblems,
  NamedRefusal,
} from "./NamedDraftStates";
import { NamedReadFailureState } from "./NamedReadFailure";
import { NewPathwayDialog } from "./NewPathwayDialog";
import { PathwayStepList } from "./PathwayStepList";
import { useScreeningRulesQuery } from "@/api/screeningRules";

import { ScreeningDocumentEditor } from "./ScreeningDocumentEditor";
import { useNamedDraft } from "./useNamedDraft";

/**
 * What the step-detail header calls a typed screening document.
 *
 * Neither "Pathway step" (which is what a prompt row is) nor "Clinical engine
 * document" (which is what a row WITHOUT a published schema is, and saying it
 * here would contradict the form underneath). Keyed on the schema so a third
 * published schema fails to compile until it is named.
 */
const SCREENING_DESCRIPTION: Record<ScreeningDocumentSchema, string> = {
  screening_steps: "Screening instructions",
  screening_diagnosis: "Screening diagnosis rules",
};

/**
 * One Pathway family default or variant, open for authoring (#643).
 *
 * Serves BOTH `pathway` and `pathway_variant`, because they are one clinical
 * family "split only for adoption identity" (`lib/librarySections.ts`) and the
 * ratified contract describes them as one model: "A Pathway is a parent
 * family/defaults asset with versioned variants." What differs between the two
 * is stated rather than structural — a variant names its parent, and its
 * breadcrumb goes through that parent's page.
 *
 * ## What replaces the generic editor, and why it had to
 *
 * Before this, both types reached `AssetEditor` → `PartsEditor`, which offers a
 * free-text "Part key" input and a writable textarea over a `structured` part's
 * raw JSON. Both are forbidden here: the key input is the manual-key escape
 * hatch the 2026-08-25 amendment rules out, and the JSON field is the "writable
 * raw JSON for engine-owned structured documents" the ratified contract lists
 * among rejected mechanisms. #642 removed the possibility from the READ side
 * too — `PathwayProtectedStep` "deliberately carries no generic `data` field" —
 * so this surface renders those steps' identity and order and states why their
 * contents are not authored here.
 *
 * ## What it does NOT do, recorded rather than papered over
 *
 *   * **No Always/Dynamic control.** `PathwaySelection.authority` has one legal
 *     value, `server`, and `PathwaySelectionPolicy` publishes no request-shaped
 *     mode or order field. Selection is derived by `adoption_preflight` from the
 *     effective runtime registration and is a fact about ONE PROJECT's pin, not
 *     about a library asset — so it is rendered where a project's pins are
 *     (`PathwaySelectionSummary`), and a switch here would be a control with no
 *     request behind it.
 *   * **No rename.** `/v1/library/assets/{assetId}` is GET-only; archive and
 *     unarchive are the only lifecycle writes the contract declares.
 *   * **No re-parenting.** `parentPathwayId` is stated at creation and is
 *     immutable thereafter; the parent is shown as provenance, never as a
 *     picker.
 *
 * Both of the last two are the same divergence `MainConcernAssetPage` already
 * records, for the same reason, and they are carried to #448 rather than
 * approximated with a disabled button.
 */
export function PathwayAssetPage({
  asset,
}: {
  /**
   * NARROWED TO THE TWO TYPES THIS PAGE SERVES, rather than `LibraryAsset`.
   *
   * The route dispatches on `assetType`, so the narrowing exists there and
   * carrying it into the prop is free — and it is what lets the copy control
   * hand `NewPathwayDialog` a `parentPathwayId` without a cast. A tenth asset
   * type in a regenerated contract cannot reach here by accident.
   */
  asset: PathwayAsset | PathwayVariantAsset;
}) {
  const state = useNamedDraft(asset);
  const manifest = useLibraryPartManifestQuery();
  const retryManifest = useRetryPausedQuery(queryKeys.libraryPartManifest);
  // The family read is what turns a variant's stored `parentPathwayId` into a
  // NAME. Archived rows are requested deliberately: this asset may itself be
  // archived, or sit under an archived default, and a family list that omitted
  // those would leave the page unable to state its own parent.
  const families = usePathwayFamiliesQuery(true);
  // THE VERSION HISTORY, for the step-id retirement mark. The list carries the
  // server-computed high-water value; fetching every immutable body here would
  // turn each page open and Save into an unbounded N-request fan-out.
  const versions = useAssetVersionsQuery(asset.id);
  // WHAT THE STORED RULES MEAN, from the service that owns the composition. The
  // criteria come from the classification schema — frequently base canon's
  // rather than this version's — and the inherited half of every threshold comes
  // from base canon's own document at this coordinate. Pinned to the version the
  // draft opened, so the description belongs to the bytes on screen.
  //
  // READ OFF `state` HERE rather than from `editor` below, because `editor` is
  // destructured after this component's early returns and a hook may not be
  // called conditionally. `null` disables the query, which is the right answer
  // while the draft has not resolved a version to describe.
  const screeningRulesVersion = state.status === "ready" ? state.editor.pinned : null;
  const screeningRules = useScreeningRulesQuery(asset.id, screeningRulesVersion);
  const retryScreeningRules = useRetryPausedQuery(
    queryKeys.screeningRules(asset.id, screeningRulesVersion ?? ""),
  );
  /**
   * The projection READ FAILED, as distinct from having no answer yet.
   *
   * `isPaused` (offline, retry held) and `isError` are two different failures
   * and neither is `isPending`, so passing `isPending` alone left the form
   * reporting a CONTENT-level refusal — "no reason was given" — for a request
   * that never arrived. The manifest read three lines up has carried this
   * distinction since #491; this is the same shape for the same reason.
   *
   * RETAINED DATA DOES NOT EXCUSE A FAILED REFRESH. TanStack Query keeps the
   * previous `data` after a refetch error, and describing a rule's effect from a
   * projection whose last read failed is exactly the unverified claim this leaf
   * removes — so the failure wins over the retained answer.
   */
  const screeningRulesFailure = screeningRules.isPaused
    ? screeningRules.failureReason
    : screeningRules.isError
      ? screeningRules.error
      : null;
  const screeningRulesUnread = screeningRules.isPaused || screeningRules.isError;
  const write = useCapability("library.write");
  const restore = useCapability("library.restore");
  const [openRole, setOpenRole] = useState<string | null>(null);
  const [lifecycle, setLifecycle] = useState<LifecycleMode | null>(null);
  const [copying, setCopying] = useState(false);

  const family = pathwayFamilyOfAsset(families.data, asset.id);
  const isVariant = asset.assetType === "pathway_variant";
  const parent = isVariant ? (family?.default ?? null) : null;

  const breadcrumbs = [
    { label: "Clinical library", to: "/library" },
    { label: "Pathways", to: "/library/pathways" },
    // A variant's page is reached THROUGH its family default, which is what the
    // parent/child model means for navigation. Present only when the family
    // read named one: a crumb pointing at an id this page could not resolve
    // would be a link to a page that may not exist.
    ...(parent
      ? [
          {
            label: parent.name,
            to: libraryAssetHref("pathway", parent.assetId),
          },
        ]
      : []),
  ];

  if (state.status === "loading") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <LoadingState label="Loading pathway content" rows={4} />
      </>
    );
  }

  if (state.status === "error") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <NamedReadFailureState
          failure={state.failure}
          isPaused={state.isPaused}
          retry={state.retry}
          subject="Asset content"
          title="This pathway's content could not be loaded"
          description={`The Editor Service did not return the content at ${asset.latestVersion}, so there is nothing to edit yet.`}
          deniedDescription="You are not authorized to read this pathway's content."
          offlineDescription={`The content at ${asset.latestVersion} could not be reached, so there is nothing to edit yet. It loads on its own when the connection returns.`}
        />
      </>
    );
  }

  if (state.status === "unsupported") {
    return (
      <>
        <PageHeader breadcrumbs={breadcrumbs} title={asset.name} />
        <UnavailableState
          title="This asset's content is not a set of steps"
          description="A pathway is authored as ordered steps, and this asset's stored content is a different shape. It is shown by its own editor instead."
        />
      </>
    );
  }

  const { editor } = state;
  const rows = namedRows(editor.parts, asset.assetType);
  const open = openRole === null ? null : findNamedRow(rows, openRole);

  // The three manifest states this surface distinguishes, and the reason
  // `MainConcernAssetPage` gives for all three: `isError` is the SETTLED half
  // only, a retry held by the retryer keeps `isPending` true with `isError`
  // false, and RETAINED DATA DOES NOT EXCUSE A FAILED REFRESH — adding from a
  // cached roster is an assertion about service policy made by a client whose
  // last read of that policy failed.
  const manifestFailure = manifest.isPaused
    ? manifest.failureReason
    : manifest.isError
      ? manifest.error
      : null;
  const manifestUnread = manifest.isPaused || manifest.isError;

  // FAIL CLOSED ON A FAILED REFRESH, and `null` is how that is spelled.
  // TanStack Query RETAINS the previous `data` after a refetch error, so
  // `manifest.data` can be a full roster at the same moment `manifestUnread` is
  // true — and every consumer below (which roles may be added, which rows stop
  // being badged engine-owned, which rows open a typed form) would then be
  // asserting service policy from a read that failed. The Add control already
  // refused on `manifestUnread`; withholding the ENTRY makes every other
  // consumer inherit that answer instead of each re-deciding it.
  const entry = manifestUnread
    ? null
    : manifestFor(manifest.data, asset.assetType);
  // THE MANIFEST'S ANSWER, APPLIED TO DRAFT ROWS. `stepCounts` prefers the
  // server's `editability` and falls back to the part kind for a row that has
  // none — which every draft row is. Supplying it here is what keeps the
  // header from reading "1 clinical engine document" beside a row that opens a
  // working typed form, the same contradiction #911 removed from the catalog.
  const typedRoles = typedScreeningRoles(entry);
  // THE ROLES THIS DRAFT MAY NOT DROP. `unremovableRoles` asks the question
  // `library.validate_parts` asks — would dropping this role leave EVERY
  // required family incomplete — rather than flattening a list of alternatives.
  const requiredRoles = unremovableRoles(
    entry,
    rows.map((row) => row.role),
  );
  // A cached history that predates the draft's pin is PARTIAL even while React
  // Query reports its retained data as successful. Require the pin (or, during
  // stale recovery, the intervening head) to appear before trusting the mark.
  const staleHead = editor.failure?.kind === "stale" ? editor.failure.version : null;
  const requiredHistoryVersion = staleHead ?? editor.pinned;
  // `isSuccess` ALONE is the fail-closed half: TanStack Query moves `status` to
  // `error` on a failed refetch even while it retains `data`, so a refused
  // history stops being current here without a second guard. An earlier round
  // added `!isTerminalQueryError(versions.error)`, which reads like a guard and
  // is dead — `error` is `null` on every success transition.
  const historyIsCurrent =
    versions.isSuccess &&
    versions.data.items.some((item) => item.version === requiredHistoryVersion);
  /**
   * THREE STATES, NOT TWO — and collapsing them was a real refusal bug.
   *
   * `undefined` is "this browser has not read the history"; `null` is the
   * SERVICE's own answer, "no stored version claims a step id", which the
   * contract states in as many words and which is the ordinary answer for a
   * Pathway whose published versions carry no screening steps yet. The first
   * round mapped both onto `null`, so a successful read of an unclaimed history
   * disabled Add for good under the message "This screening's published step
   * ids could not be read" — a read failure reported for a read that succeeded.
   */
  // THE KEY'S PRESENCE, not just its value. The contract declares this field
  // OPTIONAL (`screeningStepIdHighWater?: number | null`, because the model
  // defaults it to `None`), so a response that OMITS it — an older deployment, a
  // route that grows `response_model_exclude_none`, a proxy that strips nulls —
  // would otherwise read as the service's positive "nothing claims an id" and
  // allocate from a history this browser never saw. Absent means unread.
  const publishedStepIdHighWater: number | null | undefined =
    historyIsCurrent && "screeningStepIdHighWater" in versions.data
      ? versions.data.screeningStepIdHighWater
      : undefined;

  const loadedStepIds = screeningStepIds(editor.loaded);
  const draftStepIds = screeningStepIds(editor.parts);
  /**
   * EVERY STEP ID THIS ASSET HAS EVER CLAIMED, or `null` when that cannot be
   * established. The same union `ScreeningDocumentEditor` builds for the steps
   * form, hoisted because ADDING the role needs it too: a seeded `id: 1` that
   * ignored the mark would hand a blank new instruction an identity a retired
   * step's `mandatory_roles.stepN` may still name.
   */
  const reservedStepIds =
    publishedStepIdHighWater === undefined
      ? null
      : new Set([
          ...(publishedStepIdHighWater === null ? [] : [publishedStepIdHighWater]),
          ...loadedStepIds,
          ...referencedStepIds(editor.parts),
        ]);
  const newlyAddedStepIds = [...draftStepIds].filter(
    (id) => !loadedStepIds.has(id),
  );
  const collidingStepIds =
    publishedStepIdHighWater == null
      ? []
      : newlyAddedStepIds.filter((id) => id <= publishedStepIdHighWater);
  const rebaseRefusal =
    staleHead === null || newlyAddedStepIds.length === 0
      ? null
      : publishedStepIdHighWater === undefined
        ? "The current step-id history is still loading, so these newly added steps cannot yet be published on top of the newer version."
        : collidingStepIds.length > 0
          ? `The newer history has already claimed ${collidingStepIds.map((id) => `step ${String(id)}`).join(", ")}. Keeping this draft would assign that identity to a different instruction, so take the newer version instead.`
          : null;
  const counts = stepCounts(
    rows.map((row) => ({
      kind: row.kind,
      editability:
        row.kind === "structured" && typedRoles.has(row.role)
          ? "typed"
          : undefined,
    })),
  );

  // ONLY A ROLE A NEW VERSION COULD ACTUALLY CARRY. #911's
  // `validate_protected_documents` makes a structured role with no published
  // schema IMMUTABLE across versions: adding one to an existing asset is
  // refused outright ("cannot be added by a new version"). Offering it here
  // would be a control whose only outcome is a rejected save — and, because
  // this takes the FIRST addable role, a protected one standing at the head of
  // the roster would also hide every typed role behind it. Prompt roles and
  // roles the service publishes a schema for are what remain.
  //
  // Copying a seeded ten-file Pathway still carries its protected documents:
  // that is a CREATE, which the same function deliberately leaves alone, and it
  // goes through `NewPathwayDialog` rather than this control.
  const nextStep =
    addableRoles(entry, editor.parts).find(
      (definition) =>
        definition.kind === "prompt_text" ||
        definition.structuredSchema != null,
    ) ?? null;

  const addRefusal =
    manifestUnread || (!manifest.isPending && entry === null)
      ? "The Editor Service did not report which steps this asset type allows."
      : manifest.isPending
        ? "Still reading which steps this asset type allows."
        : nextStep === null
          ? "Every step this asset type allows that can be added here is already present."
          : screeningAddRefusal(nextStep.structuredSchema, reservedStepIds);

  const archiveMode: LifecycleMode = asset.capabilities.archived
    ? "unarchive"
    : "archive";
  // Direction-specific caller gate (#647), the same split the service enforces
  // on the two routes: archiving is `library.write`, restoring is the
  // admin/operations `library.restore` the ratified contract (§2.1) reserves.
  const archiveRefusal = lifecycleRefusal(
    asset,
    archiveMode === "unarchive" ? restore.reason : write.reason,
  );
  const archiveLabel = archiveMode === "archive" ? "Archive" : "Unarchive";

  const meta = (
    <NamedAssetMeta
      asset={asset}
      version={editor.pinned}
      // Only while the draft is pinned to the version the family read
      // described. During a stale recovery the two are different versions, and
      // a content hash from the HEAD printed beside an older version label
      // would attribute one version's bytes to another.
      {...(memberContentHash(family, asset.id, editor.pinned)
        ? { contentHash: memberContentHash(family, asset.id, editor.pinned)! }
        : {})}
      identity={`pathways/${asset.id}`}
    />
  );

  // The step editor is a screen of its own, reached by Open and returned from
  // by Back — the arrangement the canonical reference uses and that
  // `MainConcernAssetPage` already follows. A step whose documents are
  // engine-owned reaches it too, and `NamedDocumentEditor` states why it cannot
  // be authored; Save is WITHHELD there rather than disabled, because nothing
  // on that screen can make a draft dirty.
  if (open) {
    // THE SERVICE DECIDES WHICH OF THE THREE EDITORS THIS ROW GETS, and the
    // manifest is where it says so: `structuredSchema` is "how a client
    // DISCOVERS a typed form instead of hardcoding a role list (#911)". A pair
    // of role names written here instead would be the frontend Editor-API
    // catalogue #300 deleted and left a build guard against — and it would go
    // stale the moment the service publishes a third schema.
    //
    // Read from the same `entry` the Add control already uses, so a failed or
    // still-pending manifest read leaves this row on its EXISTING protected
    // explanation rather than on a typed form built from a roster this page
    // could not confirm. `PartialDataNotice` already reports that read.
    const screeningSchema =
      open.kind === "structured"
        ? screeningSchemaForRole(entry, open.role)
        : null;
    const authorable =
      isAuthorableDocumentSet(open.documents) || screeningSchema !== null;
    return (
      <>
        <PageHeader
          breadcrumbs={[
            ...breadcrumbs,
            { label: asset.name, to: libraryAssetHref(asset.assetType, asset.id) },
            { label: open.label },
          ]}
          title={open.label}
          description={
            screeningSchema !== null
              ? SCREENING_DESCRIPTION[screeningSchema]
              : authorable
                ? "Pathway step"
                : "Clinical engine document"
          }
          meta={meta}
          actions={
            <NamedDocumentActions
              readOnly={editor.readOnly || !authorable}
              dirty={editor.dirty}
              saving={editor.saving}
              onBack={() => setOpenRole(null)}
              onSave={() => editor.save()}
              backLabel="Back to steps"
            />
          }
        />
        {/* THE SAME READ FAILURE, ON THE SCREEN IT ALSO DECIDES. A failed
            manifest refresh withholds the typed form for this row, so the step
            screen has to say why it is showing the engine-owned explanation
            instead — the list screen's notice is not on this screen, and a row
            that silently changed presentation between two visits is the
            unexplained state `PartialDataNotice` exists to prevent. */}
        <PartialDataNotice
          // ONLY ON A STRUCTURED ROW. A prompt row is authorable from its kind
          // alone and its editor is right underneath, so telling a clinician it
          // "is shown as engine-owned" would be the notice contradicting the
          // working textarea below it.
          visible={manifestUnread && open.kind === "structured"}
          description="The Editor Service did not report which steps this asset type allows, so this step is shown as engine-owned until that read succeeds."
          {...(manifestUnread &&
          open.kind === "structured" &&
          !isTerminalQueryError(manifestFailure)
            ? {
                action: (
                  <Button variant="outline" onClick={retryManifest}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />
        <NamedConflictRecovery editor={editor} rebaseRefusal={rebaseRefusal} />
        {screeningSchema !== null ? (
          <ScreeningDocumentEditor
            documents={open.documents}
            schema={screeningSchema}
            parts={editor.parts}
            publishedStepIdHighWater={publishedStepIdHighWater}
            loadedParts={editor.loaded}
            screeningRules={screeningRulesUnread ? null : (screeningRules.data ?? null)}
            // THREE STATES, NOT TWO. `pending` is "no answer yet"; `failed` is
            // "the read did not arrive"; neither is the service ANSWERING that a
            // half could not be resolved. Collapsing the middle one made a
            // transport failure render as a content refusal with no reason.
            screeningRulesPending={screeningRules.isPending && !screeningRulesUnread}
            screeningRulesFailed={screeningRulesUnread}
            {...(screeningRulesUnread &&
            !isTerminalQueryError(screeningRulesFailure)
              ? { onRetryScreeningRules: retryScreeningRules }
              : {})}
            readOnly={editor.readOnly}
            readOnlyReason={editor.readOnlyReason}
            problems={editor.problems}
            onChange={(documentId, text) =>
              editor.change(updateNamedDocument(editor.parts, documentId, text))
            }
          />
        ) : (
          <NamedDocumentEditor
            documents={open.documents}
            readOnly={editor.readOnly}
            readOnlyReason={editor.readOnlyReason}
            problems={editor.problems}
            onChange={(documentId, text) =>
              editor.change(updateNamedDocument(editor.parts, documentId, text))
            }
          />
        )}
        <div className="mt-4">
          <NamedRefusal editor={editor} />
        </div>
      </>
    );
  }

  return (
    <>
      <PageHeader
        breadcrumbs={breadcrumbs}
        title={asset.name}
        description={describeSteps(counts)}
        meta={meta}
        actions={
          <>
            {asset.capabilities.seeded ? (
              <Tooltip content="Part of the base clinical library. The original stays as it is — make an editable copy to create your own version.">
                <Button variant="outline" onClick={() => setCopying(true)}>
                  Create editable copy
                </Button>
              </Tooltip>
            ) : null}
            <Tooltip content={archiveRefusal ?? `${archiveLabel} this asset`}>
              <Button
                variant="outline"
                disabled={archiveRefusal !== null}
                onClick={() => setLifecycle(archiveMode)}
              >
                {archiveLabel}
              </Button>
            </Tooltip>
          </>
        }
      />

      <div className="space-y-4">
        {/* WHERE THIS ASSET SITS IN ITS FAMILY, from the service's own parent
            key. A variant states its default; a default states how many
            variants it owns. Neither is inferred from a name, a coordinate or
            the section this page was reached through — the ratified contract
            rules all three out as identity sources. */}
        <PathwayFamilyProvenance
          asset={asset}
          family={family}
          pending={families.isPending && !families.isPaused}
          unread={families.isPaused || families.isError}
        />

        {/* A REFUSAL IN A TOOLTIP IS NOT A REPORTED FAILURE — the tooltip on the
            disabled control says why THAT control is refused, to someone who
            hovers it, while the read that failed is a fact about this page. The
            retry is offered only where it could run: a 401, 403, 404 or 501 has
            settled and asking again returns the same answer. */}
        <PartialDataNotice
          visible={manifestUnread}
          description="The Editor Service did not report which steps this asset type allows. No step can be added, structured steps are shown as engine-owned and cannot be removed, and the counts above read them the same way, until that read succeeds."
          {...(manifestUnread && !isTerminalQueryError(manifestFailure)
            ? {
                action: (
                  <Button variant="outline" onClick={retryManifest}>
                    Try again
                  </Button>
                ),
              }
            : {})}
        />

        <NamedConflictRecovery editor={editor} rebaseRefusal={rebaseRefusal} />
        <NamedRefusal editor={editor} />
        <NamedProblems editor={editor} />

        <PathwayStepList
          rows={rows}
          typedRoles={typedRoles}
          requiredRoles={requiredRoles}
          // PENDING COUNTS AS UNREAD for the removal rules, which is a stricter
          // reading than `manifestUnread`'s. That flag exists to say a read has
          // FAILED; the removal rules simply need the roster to be present, and
          // it is not while the first read is in flight.
          manifestUnread={manifestUnread || entry === null}
          readOnly={editor.readOnly}
          onOpen={setOpenRole}
          onMove={(role, to) => editor.change(moveNamedRow(editor.parts, role, to))}
          onRemove={(role) => editor.change(removeNamedRow(editor.parts, role))}
          add={{
            label: "Add step",
            refusal: addRefusal,
            onAdd: () => {
              if (!nextStep) {
                return;
              }
              const added = addNamedRole(
                editor.parts,
                nextStep.role,
                nextStep.kind as "prompt_text" | "structured",
                nextDraftId,
              );
              // A TYPED ROLE NEEDS A DOCUMENT ITS FORM CAN OPEN. `addNamedRole`
              // seeds every structured part `"{}"`, which `renderableSteps`
              // refuses — so the new row opened as "not in the shape its schema
              // describes" with no control able to repair it, and the save was
              // refused too. The seed comes from the schema the manifest named,
              // so a third published schema is served without another branch.
              const schema =
                nextStep.structuredSchema == null
                  ? null
                  : screeningSchemaForRole(entry, nextStep.role);
              const seed =
                schema === null
                  ? null
                  : emptyScreeningDocument(schema, reservedStepIds);
              // The seed can REFUSE — an unread history, or an exhausted id
              // space — and adding the role anyway would put back the dead row
              // this branch exists to prevent. `addRefusal` already withholds
              // the control in that state; this is the same answer at the one
              // place that could still act.
              if (schema !== null && seed === null) {
                return;
              }
              editor.change(
                seed === null
                  ? added
                  : added.map((part) =>
                      splitPartKey(part.key)[1] === nextStep.role &&
                      part.text === "{}"
                        ? {
                            ...part,
                            text: serializeScreeningDocument(seed),
                          }
                        : part,
                    ),
              );
            },
          }}
        />

        {editor.readOnly ? null : (
          <div className="flex flex-wrap items-center justify-end gap-3">
            <SaveStatusLine
              status={
                editor.saving
                  ? "saving"
                  : editor.failure !== null
                    ? "error"
                    : editor.dirty
                      ? "dirty"
                      : editor.lastSavedAt
                        ? "saved"
                        : "clean"
              }
              lastSavedAt={editor.lastSavedAt}
            />
            <Button
              variant="outline"
              disabled={!editor.dirty || editor.saving}
              onClick={editor.discard}
            >
              Discard
            </Button>
            <Button
              disabled={!editor.dirty || editor.saving}
              onClick={() => editor.save()}
            >
              Save
            </Button>
          </div>
        )}

        <AssetAuditTrail assetId={asset.id} />
      </div>

      {lifecycle ? (
        <LifecycleDialog
          asset={asset}
          mode={lifecycle}
          onClose={() => setLifecycle(null)}
        />
      ) : null}
      {copying ? (
        <NewPathwayDialog
          assetType={asset.assetType}
          copyFrom={
            asset.assetType === "pathway_variant"
              ? {
                  id: asset.id,
                  assetType: "pathway_variant",
                  name: asset.name,
                  latestVersion: asset.latestVersion,
                  parentPathwayId: asset.parentPathwayId,
                }
              : {
                  id: asset.id,
                  assetType: "pathway",
                  name: asset.name,
                  latestVersion: asset.latestVersion,
                }
          }
          onClose={() => setCopying(false)}
        />
      ) : null}
    </>
  );
}

/** "3 authored steps · 5 clinical engine documents", or the singular of each. */
function describeSteps({
  editable,
  protected: engineOwned,
}: {
  editable: number;
  protected: number;
}): string {
  const authored = `${editable} authored ${editable === 1 ? "step" : "steps"}`;
  if (engineOwned === 0) {
    return authored;
  }
  return `${authored} · ${engineOwned} clinical engine ${engineOwned === 1 ? "document" : "documents"}`;
}

/**
 * The content hash of this asset's head, when the draft is pinned to it.
 *
 * The families read describes each member at its HEAD, so its hash is only this
 * draft's hash while the two versions agree — during a stale recovery they do
 * not, and printing the head's bytes beside an older version label would
 * attribute one version's content to another. `undefined` rather than a guess.
 */
function memberContentHash(
  family: PathwayFamily | null,
  assetId: string,
  pinned: string,
): string | undefined {
  const member =
    family?.default.assetId === assetId
      ? family.default
      : family?.variants.find((variant) => variant.assetId === assetId);
  return member && member.version === pinned ? member.contentHash : undefined;
}

/**
 * Where this asset sits in its family, or an honest statement that it is not
 * yet known.
 *
 * FOUR STATES, NOT TWO, for the reason every read on this application
 * distinguishes them: still loading, could not be read, read and answered, and
 * — for a variant only — read but naming no family. The last is the one worth
 * spelling out: a variant whose parent the service does not group it under is
 * not "a variant with no parent", because `parentPathwayId` is required at
 * creation and immutable afterwards. It is an answer this page cannot explain,
 * so it says so and names the stored key rather than inventing a reason.
 */
function PathwayFamilyProvenance({
  asset,
  family,
  pending,
  unread,
}: {
  asset: LibraryAsset;
  family: PathwayFamily | null;
  pending: boolean;
  unread: boolean;
}) {
  const isVariant = asset.assetType === "pathway_variant";

  if (pending) {
    return (
      <p className="text-sm text-muted-foreground">
        Loading this pathway's family…
      </p>
    );
  }

  if (unread) {
    return (
      <p className="text-sm text-muted-foreground">
        This pathway's family could not be read, so its{" "}
        {isVariant ? "parent" : "variants"} are not shown. Nothing else on this
        page depends on that read.
      </p>
    );
  }

  if (isVariant) {
    const parent = family?.default;
    if (!parent) {
      return (
        <p className="text-sm text-muted-foreground">
          The shared library did not group this variant under a family default,
          so its parent is not shown here.
        </p>
      );
    }
    return (
      <p className="text-sm text-muted-foreground">
        A variant of{" "}
        <Link
          className="underline underline-offset-2"
          to={libraryAssetHref("pathway", parent.assetId)}
        >
          {parent.name}
        </Link>
        {parent.archivedAt === null
          ? "."
          : " — that family default is archived, so it can take no new version while this variant stays editable."}
      </p>
    );
  }

  const variants = family?.variants ?? [];
  return (
    <p className="text-sm text-muted-foreground">
      {variants.length === 0
        ? "This family default has no variants."
        : `This family default has ${variants.length} ${variants.length === 1 ? "variant" : "variants"}: `}
      {variants.map((variant, index) => (
        <span key={variant.assetId}>
          {index > 0 ? ", " : null}
          <Link
            className="underline underline-offset-2"
            to={libraryAssetHref("pathway_variant", variant.assetId)}
          >
            {variant.name}
          </Link>
          {variant.archivedAt === null ? null : " (archived)"}
        </span>
      ))}
      {variants.length === 0 ? null : "."}
    </p>
  );
}

/**
 * Identity for a newly added document, distinct from its key — the same reason
 * `assetContent.newDraftPart` keeps one: two keyless-equal documents mid-edit
 * would make React reuse the wrong row's DOM, including the focused control.
 */
let addedDocuments = 0;
function nextDraftId(): string {
  addedDocuments += 1;
  return `pathway-step-${addedDocuments}`;
}
